import { useHookstate } from "@hookstate/core";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { rootComponentsStore } from "../hookstate/rootComponentsStore";

const STORAGE_KEY_PREFIX = "gc-sidebar-open";

function getStorageKey(queryClient: ReturnType<typeof useQueryClient>): string {
  try {
    const accountData = queryClient.getQueryData<any>(["account-me"]);
    const userId = accountData?.data?.payload?.results?.user?.id;
    if (userId) return `${STORAGE_KEY_PREFIX}-${userId}`;
  } catch {}
  return STORAGE_KEY_PREFIX;
}

function readPersistedState(key: string): boolean | null {
  try {
    const val = localStorage.getItem(key);
    if (val !== null) return val === "true";
  } catch {}
  return null;
}

function persistState(key: string, value: boolean) {
  try { localStorage.setItem(key, String(value)); } catch {}
}

export function useIsSidebarOpen() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const initializedRef = useRef(false);

  const isSidebarOpen = useHookstate(rootComponentsStore.isSidebarOpen).get({
    noproxy: true,
  });

  // On first mount: restore from localStorage, or default collapsed for admin-next
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const key = getStorageKey(queryClient);
    const persisted = readPersistedState(key);
    if (persisted !== null) {
      rootComponentsStore.isSidebarOpen.set(persisted);
    } else if (location.pathname.startsWith("/admin-next")) {
      rootComponentsStore.isSidebarOpen.set(false);
      persistState(key, false);
    }
  }, [queryClient, location.pathname]);

  const toggleIsSidebarOpen = useCallback(() => {
    const currentValue = rootComponentsStore.isSidebarOpen.get();
    const next = !currentValue;
    rootComponentsStore.isSidebarOpen.set(next);
    persistState(getStorageKey(queryClient), next);
  }, [queryClient]);

  const setIsSidebarOpen = useCallback((value: boolean) => {
    rootComponentsStore.isSidebarOpen.set(value);
    persistState(getStorageKey(queryClient), value);
  }, [queryClient]);

  return { isSidebarOpen, toggleIsSidebarOpen, setIsSidebarOpen };
}
